// Capstone GATE A (supabase-mongo-auth-capstone.md §3) — canvas-local mirror
// of `workspace/packages/workspace/functions/claimResolver.js`, for the
// team-and-agents `_viewerTier` gate.
//
// WHY a local copy instead of importing
// `workspace/packages/workspace/functions/claimResolver.js`: this file lives
// in `packages/canvas` — a sibling consumer package that cannot reach into
// `packages/workspace/functions/*` without inverting the dependency
// direction. That is the exact cross-package barrier already documented at
// `agentAssignments.js:57`. So this is a tiny, self-contained reader that
// mirrors the workspace resolver's shape.
//
// Mongo-sourced: `is_owner` / `permissions` are sourced from the active-org
// row (`root.org`), server-enriched by `/auth/me` (org.isOwner /
// org.permissions), falling back to whatever's already on `root.jwtClaims`
// when that enrichment is absent. Was flag-gated behind
// `__USE_MONGO_CLAIMS__` during the Gate A cutover (2026-07-03); collapsed to
// Mongo-only 2026-07-31 — the governance JWT read-dependency (the flag's OFF
// branch) had no live data source since the server stopped minting
// `app_metadata` claims on 2026-07-28.
//
// SCOPE NOTE: the canvas tier reads only `is_owner` + `permissions`, so only
// those two are re-sourced here (every other claim field is preserved via the
// spread). The workspace resolver additionally derives `org_role` /
// `symbols_role` and re-computes the full permission union
// (`_unionPermissions`) on the unenriched fallback path. This mirror
// deliberately does NOT triplicate that permission model — when `root.org` is
// server-enriched (the common path) the two resolvers are identical; when it
// is not, this side falls back to the current jwtClaims permissions rather
// than re-deriving the union in a third location.

// Active-org row — mirrors the workspace resolver's `_activeOrg`: prefer
// `root.org` (populated by subscribeAuthSession / the `/auth/me` scope fetch),
// else look up `workspace_org_id` in `root.orgs`, else the first org.
const _activeOrg = (root) => {
  if (!root) return null
  if (root.org) return root.org
  const activeId = root.jwtClaims?.workspace_org_id
  const orgs = Array.isArray(root.orgs) ? root.orgs : []
  return (activeId && orgs.find((o) => o && o.id === activeId)) || orgs[0] || null
}

// THE single choke point for the canvas tier. Mongo-sourced `is_owner` /
// `permissions` scoped to the ACTIVE org. Null-safe: returns `{}` when
// there's no claim source (matching the historical `root?.jwtClaims || {}`
// posture).
export const canvasResolveClaims = (root) => {
  const base = (root && root.jwtClaims) || {}
  const org = _activeOrg(root)
  const isOwner = org && typeof org.isOwner === 'boolean' ? org.isOwner : !!base.is_owner
  let permissions
  if (org && Array.isArray(org.permissions)) permissions = org.permissions
  else if (org && Array.isArray(org.intranetPermissions)) permissions = org.intranetPermissions
  else permissions = Array.isArray(base.permissions) ? base.permissions : []
  return { ...base, is_owner: isOwner, permissions }
}
